import React from 'react'
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Select from 'react-select'

import { withTranslation } from 'react-i18next'

import FormInputField from '../../components/FormInputField'
import projectsActions from './actions'
import ProjectLogoEditor from './ProjectLogoEditor'
import GitOptionImportBranch from '../../components/GitOptions/GitOptionImportBranch'

class ProjectEditModal extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      id: null,
      name: '',
      nameIsAvailable: false,
      nameUpdated: false,
      noSelectedLanguages: false,
      paths: '',
      referenceLanguages: null,
      selectedBranch: null,
      selectedBranchName: null
    }
  }

  componentDidUpdate(prevProps) {
    const {
      isOpen: prevIsOpen,
      nameCheckingStatus: prevNameCheckingStatus,
      updateStatus: prevUpdateStatus
    } = prevProps

    const {
      isOpen,
      nameCheckingError,
      nameCheckingStatus,
      updateStatus
    } = this.props

    if (prevIsOpen === false && isOpen) this.updateStateWithProject()

    if (isOpen === false) return

    if (prevNameCheckingStatus === 'checking') {
      if (nameCheckingStatus === 'failed') {
        this.nameField.showAsInvalid(nameCheckingError)
      } else if (nameCheckingStatus === 'succeeded') {
        this.nameField.showAsValid()
      }

      this.updateNameAvailabilityStatus(nameCheckingStatus)
    }

    if (prevUpdateStatus === 'updating' && updateStatus === 'succeeded') {
      this.toggle()
    }
  }

  handleChange({ target: { name, value } }) {
    this.setState({
      [name]: value,
      nameUpdated: name === 'name'
    })
  }

  onLocalesSelect(selected) {
    this.setState({
      noSelectedLanguages: false,
      referenceLanguages: selected
    })
  }

  onSubmit(event) {
    const { dispatch, project } = this.props

    const {
      id,
      name,
      nameIsAvailable,
      nameUpdated,
      paths,
      referenceLanguages,
      selectedBranch,
      selectedBranchName
    } = this.state

    event.preventDefault()

    if (name === '') this.nameField.showAsInvalid()

    if (referenceLanguages.length === 0) {
      this.setState({
        noSelectedLanguages: true
      })
    }

    if (paths === '') this.pathsField.showAsInvalid()

    if (
      ((name && nameIsAvailable) || nameUpdated === false)
      && referenceLanguages.length > 0 && paths
    ) {
      const output = referenceLanguages.flatMap((item) => item.value)

      // Prefer explicit selection; otherwise keep current project branch
      const repositoryBranchName = selectedBranch?.label
        || selectedBranchName
        || project?.repository_branch_name

      dispatch(projectsActions.update(
        id,
        output,
        name,
        paths,
        repositoryBranchName
      ))
    }
  }

  checkNameAvailablity() {
    const { dispatch, project: { id } } = this.props
    const { name } = this.state

    if (name !== '') {
      this.nameField.showAsBusy()
      dispatch(projectsActions.checkNameAvailablity(name, id))
    }
  }

  toggle() {
    const { toggle } = this.props

    this.setState({
      id: null,
      name: '',
      nameUpdated: false,
      noSelectedLanguages: false,
      paths: '',
      referenceLanguages: null
    }, toggle())
  }

  updateNameAvailabilityStatus(status) {
    const { nameIsAvailable } = this.state

    if (nameIsAvailable === (status === 'succeeded')) return

    this.setState({
      nameIsAvailable: status === 'succeeded'
    })
  }

  updateStateWithProject() {
    const {
      locales,
      project: {
        id,
        name,
        paths,
        reference_languages: referenceLanguages,
        repository_branch_name: selectedBranchName
      }
    } = this.props

    const projectReferenceLanguages = locales.filter(
      (locale) => referenceLanguages.includes(locale.value)
    )

    this.setState({
      id,
      name,
      noSelectedLanguages: false,
      paths,
      referenceLanguages: projectReferenceLanguages,
      selectedBranch: null,
      selectedBranchName
    })
  }

  render() {
    const {
      isOpen,
      locales,
      project,
      t
    } = this.props
    const {
      name,
      paths,
      noSelectedLanguages,
      referenceLanguages,
      selectedBranch,
      selectedBranchName
    } = this.state

    const noOptionsMessage = () => (
      <p>
        {t('labels.noOptions')}
        <Link
          className="text-secondary font-weight-bold"
          name="create-locales"
          to="/locales"
        >
          {t('labels.noOptionsLink')}
        </Link>
      </p>
    )

    return (
      <Modal isOpen={isOpen} toggle={() => this.toggle()}>
        <Form id="edit">
          <ModalHeader toggle={() => this.toggle()}>
            {t('ProjectEditModal.title')}
          </ModalHeader>
          <ModalBody className="d-flex flex-column">
            <div className="d-flex align-items-center gap-3 mb-3">
              <ProjectLogoEditor
                logoDataUrl={project.logo_data_url}
                projectId={project.id}
              />
              <div className="flex-grow-1">
                <FormInputField
                  inputId="name"
                  inputName="name"
                  inputType="text"
                  labelText={t('fields.name')}
                  onChange={(event) => this.handleChange(event)}
                  onBlur={() => this.checkNameAvailablity()}
                  ref={(component) => { this.nameField = component }}
                  value={name}
                />
              </div>
            </div>
            <FormGroup className="position-relative">
              <Label for="locales">
                {t('fields.locales')}
              </Label>
              <Select
                id="locales"
                isMulti
                onChange={(locale) => this.onLocalesSelect(locale)}
                openMenuOnFocus
                options={locales}
                noOptionsMessage={() => noOptionsMessage()}
                placeholder={t('fields.localesPlaceholder')}
                value={referenceLanguages}
              />
              {noSelectedLanguages && (
                <FormFeedback className="d-block z-0" tooltip>
                  {t('errors.required_field', { ns: 'commons' })}
                </FormFeedback>
              )}
            </FormGroup>

            {/* Git branch selector (shows current project branch) */}
            <FormGroup className="position-relative">
              <Label for="git-branch">
                {t('GitOptions.options.branch.heading')}
              </Label>
              <GitOptionImportBranch
                onChange={(selected) => this.setState({ selectedBranch: selected })}
                presentation="stacked"
                repositoryId={project?.repository_id}
                selectedBranch={selectedBranch}
                selectedBranchName={selectedBranchName}
              />
            </FormGroup>

            <FormInputField
              inputFormtext={t('fields.pathsExamples')}
              inputId="paths"
              inputName="paths"
              inputPlaceholder={t('fields.pathsPlaceholder')}
              inputType="text"
              labelText={t('fields.paths')}
              onChange={(event) => this.handleChange(event)}
              ref={(component) => { this.pathsField = component }}
              value={paths}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => this.toggle()}>
              {t('buttons.cancel', { ns: 'commons' })}
            </Button>
            <Button
              color="primary"
              onClick={(event) => this.onSubmit(event)}
            >
              {t('buttons.save', { ns: 'commons' })}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    )
  }
}

ProjectEditModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  locales: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })),
  nameCheckingError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  project: PropTypes.shape({
    id: PropTypes.string,
    logo_data_url: PropTypes.string,
    name: PropTypes.string,
    paths: PropTypes.string,
    reference_languages: PropTypes.arrayOf(PropTypes.string),
    repository_id: PropTypes.number,
    repository_branch_name: PropTypes.string
  }),
  // eslint-disable-next-line react/no-unused-prop-types
  projectId: PropTypes.string,
  nameCheckingStatus: PropTypes.string,
  t: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  updateStatus: PropTypes.string
}

ProjectEditModal.defaultProps = {
  locales: null,
  nameCheckingError: null,
  nameCheckingStatus: 'idle',
  project: null,
  projectId: null,
  updateStatus: 'idle'
}

const mapStateToProps = (state, ownProps) => {
  const { locales: { items: locales }, projects } = state
  const {
    items,
    nameCheckingError,
    nameCheckingStatus,
    updateStatus
  } = projects

  // Get fresh project from Redux items if projectId is provided
  const project = ownProps.projectId && items
    ? items.find((p) => p.id === ownProps.projectId) || ownProps.project
    : ownProps.project

  return {
    locales,
    nameCheckingError,
    nameCheckingStatus,
    project,
    updateStatus
  }
}

export default withTranslation(['ProjectsPage', 'commons'])(
  connect(mapStateToProps)(ProjectEditModal)
)
